"use strict";

import {computed, provide, ref, toValue} from "vue";
import {useMetaTagJsonContent} from "../Composables/useMetaTagJsonContent";
import cloneDeepWith from "lodash.clonedeepwith";
import {defineStore} from "pinia";
import {useLoginStatus} from "../Composables/useLoginStatus";
import isEqual from "lodash.isequal";
import {forEachSiteInspectionElement} from "../Utils/siteInspectionUtil";
import {debounce} from "../utils";
import rootLogger from "../log";

const log = rootLogger.getLogger('EventSourcedStore.siteInspection');

class Event
{
    constructor({type, owner, occurrence, data})
    {
        if (type === undefined || owner === undefined) {
            throw new Error(`You have to define at least type and owner.`);
        }
        if (typeof occurrence === 'string' || occurrence instanceof String) {
            occurrence = new Date(occurrence);
        }
        if (!(occurrence instanceof Date)) {
            occurrence = new Date();
        }

        this._type = type;
        this._owner = owner;
        this._occurrence = occurrence;
        this._data = data;
    }

    get id()
    {
        return `${this._occurrence.getTime()}-${this._owner}`;
    }

    get type()
    {
        return this._type;
    }

    get owner()
    {
        return this._owner;
    }

    get occurrence()
    {
        return this._occurrence;
    }

    get data()
    {
        return this._data;
    }

    toString()
    {
        return `${this.id}|${this.type}: ${JSON.stringify(this.data)}`;
    }

    valueOf()
    {
        return {
            id: this.id,
            owner: this.owner,
            occurrence: this.occurrence,
            data: this.data,
            type: this.type,
        };
    }
}


class EventList
{
    constructor() {
        this._events = ref([]);
        this._eventEmitter = document.createElement('i');
    }

    async addEvent({type, owner, occurrence, data})
    {
        // removes metadata which would redundant on event stream
        if (data !== null && data !== undefined) {
            delete data.metadata;
        }

        const event = new Event({type, owner, occurrence, data});
        this._events.value.push(event);
        this._events.value.sort((a, b) => a.id - b.id);
        this._eventEmitter.dispatchEvent(new CustomEvent("added", {detail: event}));
    }

    restart()
    {
        this._events.value = [];
    }

    get events()
    {
        return this._events;
    }

    get eventEmitter()
    {
        return this._eventEmitter;
    }

    toString()
    {
        return toValue(this.events).map(e => e.toString());
    }
}

class SiteInspectionProjector
{
    constructor(owner) {
        if (owner === null) {
            throw new Error('You have to set an owner for projector.');
        }
        this.siteInspection = ref({});
        this.restart();
        this.owner = owner;
    }

    setLocationUuidAndCircuitUuid(event) {
        if (event.data.locationIndex !== undefined && event.data.locationIndex !== null && event.data.locationUuid === undefined) {
            event.data.locationUuid = this.siteInspection.value.locations[event.data.locationIndex]?.uuid;
        }
        // delete (event.data.locationIndex); NOTE: the deletion breaks the approving
        if (event.data.circuitIndex !== undefined && event.data.circuitIndex !== null && event.data.circuitUuid === undefined) {
            event.data.circuitUuid = this.siteInspection.value.circuits[event.data.circuitIndex]?.uuid;
        }
        // delete (event.data.circuitIndex); NOTE: the deletion breaks the approving
    }

    restart() {
        log.debug(`restarting projection: ${new Date()}`);
        this.siteInspection.value = {
            additionalInformation: {},
            locations: [],
            circuits: [],
            examinations: [],
            labels: [],
            notes: [],
            circuitComponents: [],
            timeTracks: [],
        };
        this.removed = {
            locations: [],
            circuits: [],
            examinations: [],
            labels: [],
            notes: [],
            circuitComponents: [],
            timeTracks: [],
        };
    }

    handleEvent(event)
    {
        const handleName = `handle${event.type}Event`;
        if (this[handleName] === undefined) {
            log.error(JSON.stringify({desc: `method not found "handle${event.type}Event"`, event}));
            return;
        }
        this[handleName](event);
    }

    handleAddEvent(event, root)
    {
        if (this.siteInspection.value[root] === undefined) {
            this.siteInspection.value[root] = [];
        }

        if (event.data.uuid === undefined || event.data.uuid === null) {
            log.warn(`no uuid while adding element to ${root} (therefore using event.id "${event.id}"). event: ${JSON.stringify(event)}`);
            event.data.uuid = event.id ?? crypto.randomUUID();
        }
        this.setLocationUuidAndCircuitUuid(event);

        const element = cloneDeepWith(event.data);
        if (element.metadata === undefined) {
            element.metadata = {sync: {createdBy: null}};
        }
        if (element.metadata.sync?.createdBy === undefined || element.metadata.sync?.createdBy === null) {
            element.metadata.sync.createdBy = event.owner;
        }
        if (root === 'examinations') {
            if (event.data.circuitExamination) {
                delete event.data.locationUuid;
            } else {
                delete event.data.circuitUuid;
            }
            element.images?.forEach(image => {
                if (image.createdBy === undefined) {
                    image.createdBy = event.owner;
                }
            });
        }
        if (root !== 'locations' && root !== 'circuits') {
            if (this.siteInspection.value.locations.find(location => location.uuid === element.locationUuid) === undefined) {
                element.locationUuid = this.siteInspection.value.locations[element.locationIndex]?.uuid;
            }
            if (element.locationUuid !== undefined) {
                element.locationIndex = this.siteInspection.value.locations.findIndex(location => location.uuid === element.locationUuid);
            }
            if (this.siteInspection.value.circuits.find(circuit => circuit.uuid === element.circuitUuid) === undefined) {
                element.circuitUuid = this.siteInspection.value.circuits[element.circuitIndex]?.uuid;
            }
            if (element.circuitUuid !== undefined) {
                element.circuitIndex = this.siteInspection.value.circuits.findIndex(circuit => circuit.uuid === element.circuitUuid);
            }
        }
        let index;
        for (const location of this.siteInspection.value.locations) {
            const innerIndex = this.siteInspection.value.locations.indexOf(location);
            if (location.building === element.building) {
                index = innerIndex;
            }
        }
        if (index !== undefined && !this.ignoreSort) {
            this.siteInspection.value[root].splice(index + 1, 0, element);
        } else {
            this.siteInspection.value[root].push(element);
        }
    }

    handleEditEvent(event, root)
    {
        if (event.data?.uuid === undefined) {
            log.warn(`no uuid to edit the ${root} element.`, event);
            return;
        }
        const allFindings = this.siteInspection.value[root].filter(element => element.uuid === event.data?.uuid);
        if (allFindings.length === 0) {
            log.warn(`no element found to edit, searched for event: ${JSON.stringify(event)}`);
            return;
        }

        this.setLocationUuidAndCircuitUuid(event);
        let found;
        if (allFindings.length > 1) {
            log.warn(`found more than one candidate for event: ${event.data.uuid}; found: ${allFindings.length} candidates`);
            for (const candidate of allFindings) {
                if (
                    candidate.locationUuid === event.data.locationUuid
                    && candidate.circuitUuid === event.data.circuitUuid
                ) {
                    found = candidate;
                }
            }

            if (found === undefined) {
                found = allFindings[0];
            }
        } else {
            found = allFindings[0];
        }

        const difference = calculateInnerDifference(found, event.data, false);
        delete(difference.locationIndex);
        delete(difference.circuitIndex);
        delete(difference.uuid);
        if (root === 'examinations') {
            if (Array.isArray(found.variables) && found.variables.length === 0) {
                found.variables = {};
            }
            difference.variables = Object.assign(found.variables, difference.variables);
        }
        for (const [key, value] of Object.entries(difference)) {
            found[key] = cloneDeepWith(value);
        }
        if (event.data.dirty === undefined && found.dirty !== undefined && found.dirty !== null && Object.keys(difference).length > 0) {
            found.dirty = false;
        }
        if (root === 'examinations') {
            if (found.circuitExamination) {
                delete found.locationUuid;
            } else {
                delete found.circuitUuid;
            }
            found.images?.forEach(image => {
                if (image.createdBy === undefined) {
                    image.createdBy = event.owner;
                }
            });
        }
        if (this.siteInspection.value.locations.find(location => location.uuid === found.locationUuid) === undefined) {
            found.locationUuid = this.siteInspection.value.locations[found.locationIndex]?.uuid;
        }
        if (found.locationUuid !== undefined) {
            found.locationIndex = this.siteInspection.value.locations.findIndex(location => location.uuid === found.locationUuid);
        }
        if (this.siteInspection.value.circuits.find(circuit => circuit.uuid === found.circuitUuid) === undefined) {
            found.circuitUuid = this.siteInspection.value.circuits[found.circuitIndex]?.uuid;
        }
        if (found.circuitUuid !== undefined) {
            found.circuitIndex = this.siteInspection.value.circuits.findIndex(circuit => circuit.uuid === found.circuitUuid);
        }
    }

    handleRemoveEvent(event, root)
    {
        this.removed[root].push(event.data.uuid);
        this.siteInspection.value[root].splice(this.siteInspection.value[root].findIndex(element => element.uuid === event.data.uuid), 1);
    }

    handleMigrationErrorEvent(event)
    {
        this.siteInspection.value.error = event.data.error;
    }

    handleMigratedEvent(event)
    {
    }

    handleInnerMigrationEvent(event)
    {
        forEachSiteInspectionElement(event.data.removed, (element, root) => {
            const found = this.siteInspection.value[root].find(e => e.uuid === element);
            this.handleEvent({owner: event.owner, data: found, type: `Remove${root[0].toUpperCase() + root.substr(1, root.length - 2)}`});
        });

        forEachSiteInspectionElement(event.data, (element, root) => {
            if (this.removed[root].includes(element.uuid)) {
                return;
            }
            const found = this.siteInspection.value[root].find(e => e.uuid === element.uuid);
            if (found === undefined) {
                this.handleEvent({owner: event.owner, data: element, type: `Add${root[0].toUpperCase() + root.substr(1, root.length - 2)}`});
            } else {
                if (root[0].toUpperCase() + root.substr(1, root.length - 2) !== 'Circuit') {
                    // NOTE: moving elements is only possible if it is a circuit
                    delete element.circuitUuid;
                    delete element.circuitIndex;
                    delete element.locationIndex;
                    delete element.locationUuid;
                }
                this.handleEvent({owner: event.owner, data: element, type: `Edit${root[0].toUpperCase() + root.substr(1, root.length - 2)}`});
            }
        });
    }

    handleInitDataEvent(event)
    {
        forEachSiteInspectionElement(event.data.siteInspection?.previousData ?? {}, (element, root) => {
            if (element.locationIndex || element.circuitIndex) {
                this.ignoreSort = true;
            }
        });
        forEachSiteInspectionElement(event.data.data ?? {}, (element, root) => {
            if (element.locationIndex || element.circuitIndex) {
                this.ignoreSort = true;
            }
        });
        forEachSiteInspectionElement(event.data.siteInspection?.previousData ?? {}, (element, root) => {
            const found = this.siteInspection.value[root].find(e => e.uuid === element.uuid);
            if (element.synced) {
                delete element.synced;
            }
            if (isEqual(found, element)) {
                return;
            }
            if (found === undefined) {
                this.handleEvent({owner: event.owner, data: element, type: `Add${root[0].toUpperCase() + root.substr(1, root.length - 2)}`});
            }
        });
        forEachSiteInspectionElement(event.data.data ?? {}, (element, root) => {
            if (element.synced) {
                delete element.synced;
            }
            const found = this.siteInspection.value[root].find(e => e.uuid === element.uuid);
            const previousFound = event.data.siteInspection?.previousData?.[root]?.find(e => e.uuid === element.uuid);

            if (previousFound?.uuid !== undefined && element.uuid !== undefined) {
                const diff = calculateInnerDifference(previousFound, element);
                this.setLocationUuidAndCircuitUuid({data: diff});
                if (isEqual({uuid: element.uuid}, diff)) {
                    log.debug(JSON.stringify({
                        desc: `${element.uuid}: skipped`,
                        element,
                        root,
                        previousFound,
                        diff,
                    }));
                    return;
                }
            }
            if (found === undefined) {
                this.handleEvent({owner: event.owner, data: element, type: `Add${root[0].toUpperCase() + root.substr(1, root.length - 2)}`});
            } else {
                this.handleEvent({owner: event.owner, data: element, type: `Edit${root[0].toUpperCase() + root.substr(1, root.length - 2)}`});
            }
        });
        if (event.data.data?.additionalInformation) {
            this.siteInspection.value.additionalInformation = event.data.data.additionalInformation;
        }
        this.siteInspection.value.currentLocationUuid = event.data.currentLocationUuid ?? this.siteInspection.value?.locations?.[0]?.uuid ?? null;
        this.siteInspection.value.currentCircuitUuid = event.data.currentCircuitUuid
            ?? this.siteInspection.value?.circuits?.filter(c => c.locationUuid === this.siteInspection.value.currentLocationUuid)?.[0]?.uuid
            ?? null;
        if (this.owner === event.owner || this.siteInspection.value.siteInspection === undefined) {
            this.siteInspection.value.siteInspection = event.data.siteInspection;
        }
        this.ignoreSort = false;
    }

    handleSwitchCurrentPositionEvent(event)
    {
        if (event.owner === this.owner || event.owner === this.owner.value) {
            this.siteInspection.value.currentCircuitUuid = undefined;
            if (this.siteInspection.value.circuits.find(c => c.uuid === event.data.currentCircuitUuid) !== undefined) {
                this.siteInspection.value.currentCircuitUuid = event.data.currentCircuitUuid;
            }
            this.siteInspection.value.currentLocationUuid = event.data.currentLocationUuid
                ?? this.siteInspection.value.circuits.find(c => c.uuid === this.siteInspection.value.currentCircuitUuid)?.locationUuid
                ?? this.siteInspection.value.currentLocationUuid;
            if (this.siteInspection.value.locations.find(l => l.uuid === this.siteInspection.value.currentLocationUuid) === undefined) {
                const currentCircuit = this.siteInspection.value.circuits.find(c => c.uuid === event.data.currentCircuitUuid);
                this.siteInspection.value.currentLocationUuid = this.siteInspection.value.locations[currentCircuit?.locationIndex]?.uuid;
            }
        }
    }

    handleAddLocationEvent(event)
    {
        if (event.data.roles === null) {
            event.data.roles = '';
        }
        this.handleAddEvent(event, 'locations');
        if (event.owner === this.owner) {
            this.siteInspection.value.currentLocationUuid = event.data.uuid;
            this.siteInspection.value.currentCircuitUuid = null;
        }
    }

    handleEditLocationEvent(event)
    {
        if (event.data.roles === null) {
            event.data.roles = '';
        }
        this.handleEditEvent(event, 'locations');
    }

    handleRemoveLocationEvent(event)
    {
        const locationUuid = event.data.uuid;
        for (const circuit of this.siteInspection.value.circuits.filter(circuit => circuit.locationUuid === locationUuid)) {
            this.handleRemoveCircuitEvent({data: circuit, owner: event.owner});
        }
        for (const examination of this.siteInspection.value.examinations.filter(examination => examination.locationUuid === locationUuid)) {
            this.handleRemoveExaminationEvent({data: examination, owner: event.owner});
        }

        const locationIndex = this.siteInspection.value.locations.map(l => l.uuid).indexOf(locationUuid);
        this.handleRemoveEvent(event, 'locations');
        this.siteInspection.value.currentLocationUuid = this.siteInspection.value.locations[locationIndex > 0 ? locationIndex - 1 : 0]?.uuid;
    }

    handleRemoveBuildingEvent(event)
    {
        for (const location of this.siteInspection.value.locations.filter(location => location.building === event.data.building)) {
            this.handleRemoveLocationEvent({data: location, owner: event.owner});
        }
    }

    handleAddCircuitEvent(event)
    {
        this.handleAddEvent(event, 'circuits');
        if (event.owner === this.owner) {
            this.siteInspection.value.currentCircuitUuid = event.data.uuid;
            this.siteInspection.value.currentLocationUuid = event.data.locationUuid;
        }
    }

    handleEditCircuitEvent(event)
    {
        this.handleEditEvent(event, 'circuits');
    }

    handleRemoveCircuitEvent(event)
    {
        const circuitUuid = event.data.uuid,
            containedCircuitComponents = this.siteInspection.value.circuitComponents.filter(circuitComponent => circuitComponent.circuitUuid === circuitUuid),
            containedLabels = this.siteInspection.value.labels.filter(label => label.circuitUuid === circuitUuid),
            containedExaminations = this.siteInspection.value.examinations.filter(examination => examination.circuitUuid === circuitUuid && examination.circuitExamination);

        for (let circuitComponent of containedCircuitComponents) {
            this.handleRemoveCircuitComponentEvent({data: {uuid: circuitComponent.uuid}}, 'circuitComponents');
        }
        for (let examination of containedExaminations) {
            this.handleRemoveExaminationEvent({data: {uuid: examination.uuid}}, 'examinations');
        }
        for (let label of containedLabels) {
            this.handleRemoveLabelEvent({data: {uuid: label.uuid}}, 'labels');
        }

        this.handleRemoveEvent(event, 'circuits');
        this.siteInspection.value.currentCircuitUuid = null;
    }

    handleAddCircuitComponentEvent(event)
    {
        if (event.data.circuitUuid === undefined || this.siteInspection.value.circuits.find(c => c.uuid === event.data.circuitUuid)) {
            this.handleAddEvent(event, 'circuitComponents');
        } else {
            log.warn(JSON.stringify({desc: 'the circuit component does not fit into the site inspection', event}));
        }
    }

    handleEditCircuitComponentEvent(event)
    {
        this.handleEditEvent(event, 'circuitComponents');
    }

    handleRemoveCircuitComponentEvent(event)
    {
        this.handleRemoveEvent(event, 'circuitComponents');
    }

    handleAddExaminationEvent(event)
    {
        try {
            if (event.data.circuitExamination === false) {
                delete event.data.circuitUuid;
                delete event.data.circuitIndex;
            } else {
                delete event.data.locationUuid;
                delete event.data.locationIndex;
            }
        } catch (e) {}
        if (
            (event.data.circuitUuid === undefined || this.siteInspection.value.circuits.find(c => c.uuid === event.data.circuitUuid))
            && (event.data.locationUuid === undefined || this.siteInspection.value.locations.find(c => c.uuid === event.data.locationUuid))
        ) {
            this.handleAddEvent(event, 'examinations');
        } else {
            log.warn(JSON.stringify({desc: 'the examination does not fit into the site inspection', event}));
        }
    }

    handleEditExaminationEvent(event)
    {
        try {
            if (event.data.circuitExamination === false) {
                delete event.data.circuitUuid;
                delete event.data.circuitIndex;
            }
        } catch (e) {}
        this.handleEditEvent(event, 'examinations');
    }

    handleRemoveExaminationEvent(event)
    {
        this.handleRemoveEvent(event, 'examinations');
    }

    handleAddLabelEvent(event)
    {
        this.handleAddEvent(event, 'labels');
    }

    handleEditLabelEvent(event)
    {
        this.handleEditEvent(event, 'labels');
    }

    handleRemoveLabelEvent(event)
    {
        this.handleRemoveEvent(event, 'labels');
    }

    handleAddNoteEvent(event)
    {
        this.handleAddEvent(event, 'notes');
    }

    handleEditNoteEvent(event)
    {
        this.handleEditEvent(event, 'notes');
    }

    handleAddTimeTrackEvent(event)
    {
        this.handleAddEvent(event, 'timeTracks');
    }

    handleEditTimeTrackEvent(event)
    {
        this.handleEditEvent(event, 'timeTracks');
    }

    handleRemoveTimeTrackEvent(event)
    {
        this.handleRemoveEvent(event, 'timeTracks');
    }

    handleSynchronizedEvent(event) {
    }

    get projection()
    {
        return this.siteInspection;
    }
}

function calculateDifference(oldElement, newElement)
{
    const diff = cloneDeepWith(newElement);
    if (oldElement.__identity === undefined || newElement.__identity === undefined) {
        throw new Error('Elements must have identities');
    }
    if (oldElement.__identity !== newElement.__identity) {
        throw new Error('Elements must have the same identities');
    }

    if (oldElement.data === null) {
        oldElement = {data: oldElement.siteInspection.previousData};
    }

    for (const [key, value] of Object.entries(newElement.data)) {
        if (['tester', 'additionalInformation', 'migrations'].includes(key)) {
            continue;
        }
        if (!(value instanceof Array)) {
            log.error(`data values must be arrays (${typeof value})`, value);
            continue;
        }
        const newArray = [];
        for (const innerValue of value) {
            if (!(innerValue instanceof Object)) {
                log.error(`innerValue must be an Object (${typeof innerValue})`, innerValue);
                continue;
            }
            if (oldElement.data[key]?.map(e => e.uuid).includes(innerValue.uuid)) {
                const calculatedDiff = calculateInnerDifference(oldElement.data[key].find(e => e.uuid === innerValue.uuid), innerValue);
                if (Object.keys(calculatedDiff).length > 1) {
                    newArray.push(calculatedDiff);
                }
            } else {
                newArray.push(innerValue);
            }
        }
        if (newArray.length > 0) {
            diff.data[key] = newArray;
        } else {
            delete diff.data[key];
        }
    }

    for (const [key, value] of Object.entries(oldElement.data)) {
        if (['tester', 'additionalInformation', 'migrations'].includes(key)) {
            continue;
        }
        if (!(value instanceof Array)) {
            log.warn(`data values must be arrays (${typeof value})`, value);
            continue;
        }
        for (const innerValue of value) {
            if (!(innerValue instanceof Object)) {
                log.warn(`innerValue must be an Object (${typeof innerValue})`, innerValue);
                continue;
            }
            if (newElement.data[key].find(nE => nE.uuid === innerValue.uuid) === undefined) {
                if (diff.data.removed === undefined) {
                    diff.data.removed = {};
                }
                if (diff.data.removed[key] === undefined) {
                    diff.data.removed[key] = [];
                }
                diff.data.removed[key].push(innerValue.uuid);
            }
        }
    }

    return diff;
}

function calculateInnerDifference(oldElement, newElement, withUuid = true)
{
    if (newElement instanceof Array && withUuid) {
        throw new Error('Arrays have no uuid');
    }
    const diff = {};
    if (withUuid) {
        if (oldElement.uuid === undefined || newElement?.uuid === undefined) {
            throw new Error('Elements must have uuids');
        }
        if (oldElement.uuid !== newElement.uuid) {
            throw new Error('Elements must have the same uuids');
        }
        diff.uuid = newElement.uuid;
    }

    const keys = new Set();
    Object.keys(oldElement).forEach(key => keys.add(key));
    Object.keys(newElement).forEach(key => keys.add(key));
    keys.delete('metadata');
    keys.delete('createdBy'); // NOTE: deprecated only needed due old WIP but at least on staging there is data needed
    for (const key of keys) {
        if (!(Object.keys(newElement).includes(key))) {
            continue;
        }
        if (newElement[key] instanceof Object && !(newElement[key] instanceof Array)) {
            const innerDiff = calculateInnerDifference(oldElement[key], newElement[key], false);
            if (Object.keys(innerDiff).length > 0) {
                diff[key] = cloneDeepWith(innerDiff);
            }
            continue;
        }
        if (newElement[key] instanceof Array && oldElement[key] instanceof Array && isEqual(newElement[key], oldElement[key])) {
            continue;
        }
        if (oldElement[key] !== newElement[key]) {
            diff[key] = cloneDeepWith(newElement[key]);
        }
    }

    return diff;
}

function getAccountForTester(testerIdOrObject, testers)
{
    if (testerIdOrObject?.account?.__identity) {
        return testerIdOrObject.account.__identity;
    } else {
        let tester = testers.find(tester => tester.__identity === testerIdOrObject);
        if (tester === undefined) {
            tester = testers.find(tester => tester.account.__identity === testerIdOrObject);
        }
        if (tester === undefined) {
            tester = testers.find(tester => tester.account.accountIdentifier === testerIdOrObject);
        }
        return tester.account.__identity;
    }
}

function localStorageDataTranspiled(localStorageSiteInspectionData) {
    const data = {data: cloneDeepWith(localStorageSiteInspectionData)};
    data.__identity = data.data.__identity;
    delete(data.data.__identity);
    data.siteInspection = data.data.siteInspection;
    delete data.data.siteInspection;
    data.testingIds = data.data.testingIds;
    delete data.data.testingIds;
    delete data.data.currentLocationIndex;
    delete data.data.currentCircuitIndex;
    return data;
}

class SiteInspectionIndexedStore
{
    #dbs = {};
    #dbPromises = {};
    siteInspectionStoreName = 'SiteInspections';
    metaDbStoreName = 'MetaDb';

    constructor(testerSiteInspectionDatas, testers)
    {
        const dbRequest = indexedDB.open(this.metaDbStoreName, 1),
            {promise: metaDbPromise, resolve} = Promise.withResolvers();
        this.#dbPromises[this.metaDbStoreName] = metaDbPromise;
        this.testers = testers;
        dbRequest.onupgradeneeded = (event) => {
            this.#dbs[this.metaDbStoreName] = event.target.result;

            if (event.oldVersion < 1) {
                const eventStore = this.#dbs[this.metaDbStoreName].createObjectStore(this.siteInspectionStoreName, {keyPath: 'id'});

                eventStore.createIndex('owner', 'owner', {unique: false});
                eventStore.createIndex('id', 'id', {unique: false});
            }
        };
        dbRequest.onsuccess = (event) => {
            this.#dbs[this.metaDbStoreName] = event.target.result;
            resolve(this.#dbs[this.metaDbStoreName]);
        };
        this.initializeDbForSiteInspectionData(testerSiteInspectionDatas);
    }

    async initializeDbForSiteInspectionData(testerSiteInspectionDatas, initEventName = 'InitData', occurrence = null)
    {
        const allPromises = Object.values(this.#dbs);
        const {validStatus} = useLoginStatus();
        const loginStatus = await validStatus;
        for (const testerSiteInspectionData of testerSiteInspectionDatas) {
            if (this.#dbPromises[testerSiteInspectionData.__identity] !== undefined && initEventName === 'InitData') {
                continue;
            }
            const owner = getAccountForTester(testerSiteInspectionData.tester, toValue(this.testers));
            this.#dbPromises[this.metaDbStoreName].then(async db => {
                log.log({
                    desc: 'SiteInspectionIndexedStore::constructor::metaDbPromise.then',
                    validStatus,
                    status: loginStatus.value
                });
                db.transaction([this.siteInspectionStoreName], 'readwrite')
                    .objectStore(this.siteInspectionStoreName)
                    .add({owner: loginStatus.value.account, id: testerSiteInspectionData.__identity});
            });
            const dbRequest = indexedDB.open('SiteInspections' + testerSiteInspectionData.__identity);
            const storeName = `eventStore${testerSiteInspectionData.__identity}`;
            const {promise, resolve} = Promise.withResolvers();
            allPromises.push(promise);
            this.#dbPromises[testerSiteInspectionData.__identity] = promise;

            dbRequest.onsuccess = async (event) => {
                this.#dbs[testerSiteInspectionData.__identity] = event.target.result;

                const {promise: innerPromise, resolve: innerResolve} = Promise.withResolvers();
                allPromises.push(innerPromise);
                const eventStore = this.#dbs[testerSiteInspectionData.__identity].transaction([storeName], 'readwrite').objectStore(storeName);
                eventStore.index('type').getAll('InitData').onsuccess = async (event) => {
                    if (
                        !event.target.result
                        || event.target.result instanceof Array && event.target.result.find(foundEvent => foundEvent.owner === loginStatus.value.account) === undefined
                    ) {
                        if (testerSiteInspectionData.data?.version) {
                            for (const dataEvent of testerSiteInspectionData.data.data) {
                                const eventToStore = cloneDeepWith(new Event(dataEvent).valueOf());
                                const {promise: putPromise, resolve: putResolve, reject: putReject} = Promise.withResolvers();
                                const request = this.#dbs[testerSiteInspectionData.__identity]
                                    .transaction([storeName], 'readwrite')
                                    .objectStore(storeName)
                                    .add(eventToStore);
                                request.onsuccess = putResolve(eventToStore);
                                request.onerror = putReject(eventToStore);
                                await putPromise;
                            }
                            innerResolve();
                        } else {
                            this.#dbs[testerSiteInspectionData.__identity].transaction([storeName], 'readwrite').objectStore(storeName).add((new Event({
                                type: initEventName,
                                owner,
                                data: cloneDeepWith(testerSiteInspectionData),
                                occurrence: new Date(occurrence ?? testerSiteInspectionData.creationDate),
                            })).valueOf()).onsuccess = innerResolve;
                        }
                    } else {
                        if (initEventName === 'InnerMigration') {
                            const changedData = calculateDifference(event.target.result.find(foundEvent => foundEvent.owner === loginStatus.value.account)?.data, localStorageDataTranspiled(testerSiteInspectionData));
                            this.#dbs[testerSiteInspectionData.__identity].transaction([storeName], 'readwrite').objectStore(storeName).add((new Event({
                                type: initEventName,
                                owner,
                                data: changedData.data,
                                occurrence: new Date(occurrence ?? testerSiteInspectionData.creationDate),
                            })).valueOf()).onsuccess = innerResolve;
                        } else {
                            let dataEvent = event.target.result.find(result => result.owner === loginStatus.value.account);
                            if (dataEvent === undefined && event.target.result.length === 1) {
                                dataEvent = event.target.result.find(result => result.owner === null);
                            }
                            if (dataEvent?.owner === null) {
                                const {promise: deletePromise, resolve: deleteResolve} = Promise.withResolvers();
                                this.#dbs[testerSiteInspectionData.__identity]
                                    .transaction([storeName], 'readwrite')
                                    .objectStore(storeName)
                                    .delete(dataEvent.id)
                                    .onsuccess = deleteResolve;
                                await deletePromise;
                                dataEvent.owner = dataEvent.data.tester.account.__identity;
                                this.#dbs[testerSiteInspectionData.__identity]
                                    .transaction([storeName], 'readwrite')
                                    .objectStore(storeName)
                                    .put(new Event(dataEvent).valueOf())
                                    .onsuccess = innerResolve;
                            } else {
                                innerResolve();
                            }
                        }
                    }
                };
                await innerPromise;
                resolve(this.#dbs[testerSiteInspectionData.__identity]);
            };
            dbRequest.onupgradeneeded = async (event) => {
                this.#dbs[testerSiteInspectionData.__identity] = event.target.result;

                const eventStore = this.#dbs[testerSiteInspectionData.__identity].createObjectStore(`eventStore${testerSiteInspectionData.__identity}`, {keyPath: 'id'});

                eventStore.createIndex('occurrence', 'occurrence', {unique: false});
                eventStore.createIndex('owner', 'owner', {unique: false});
                eventStore.createIndex('type', 'type', {unique: false});
            };
            dbRequest.onerror = (event) => {
                log.log({desc: 'error event from dbRequest', event});
            };
        }
        return Promise.all(allPromises);
    }

    async saveEvent(identity, event)
    {
        const db = await this.#dbPromises[identity],
            {promise, resolve, reject} = Promise.withResolvers();
        const storeName = `eventStore${identity}`;
        const request = db.transaction([storeName], 'readwrite').objectStore(storeName).add(event.valueOf());
        request.onsuccess = (event) => {
            resolve(event);
        };
        request.onerror = (event) => {
            reject(event);
        };

        return promise;
    }

    async removeSiteInspection(identity)
    {
        if (!Object.keys(this.dbs).includes(toValue(identity))) {
            throw new Error('There is no objectStorage known with this identity.');
        }
        const metaDb = await this.dbs[this.metaDbStoreName],
            {promise, resolve, reject} = Promise.withResolvers(),
            {promise: deleteDbPromise, resolve: deleteDbResolve, reject: deleteDbReject} = Promise.withResolvers();
        let request = metaDb.transaction([this.siteInspectionStoreName], 'readwrite')
            .objectStore(this.siteInspectionStoreName)
            .delete(toValue(identity));
        request.onsuccess = resolve;
        request.onerror = reject;
        await promise;

        request = indexedDB.deleteDatabase(this.siteInspectionStoreName + toValue(identity));
        request.onsuccess = deleteDbResolve;
        request.onerror = deleteDbReject;
        this.dbs[toValue(identity)].then(db => db.close()); // NOTE: open DBs can't be deleted

        return deleteDbPromise;
    }

    /**
     * @returns {{[key: string]: Promise<IDBDatabase>}}
     */
    get dbs()
    {
        return this.#dbPromises;
    }
}

const useSiteInspectionStore = defineStore('siteInspection', () => {
    const owner = ref(''),
        __identity = ref(''),
        additionalInformation = computed(() => projector.projection.value?.additionalInformation),
        circuitComponents = computed(() => projector.projection.value?.circuitComponents ?? []),
        circuits = computed(() => projector.projection.value?.circuits ?? []),
        error = computed(() => projector.projection.value?.error),
        examinations = computed(() => projector.projection.value?.examinations ?? []),
        initializing = ref(true),
        labels = computed(() => projector.projection.value?.labels ?? []),
        labelSettingsTypes = useMetaTagJsonContent('labelSettingsTypes').value,
        locations = computed(() => projector.projection.value?.locations ?? []),
        migrations = computed(() => projector.projection.value?.migrations),
        notes = computed(() => projector.projection.value?.notes ?? []),
        innerSiteInspection = computed(() => projector.projection.value?.siteInspection),
        timeTracks = computed(() => projector.projection.value?.timeTracks ?? []),
        eventList = new EventList(),
        ownEventEmitter = document.createElement('i');

    let
        projector = new SiteInspectionProjector(owner),
        saveHandler = undefined,
        eventHandler = undefined,
        store = undefined;

    const labelTypes = computed(() => {
        let types = new Set(cloneDeepWith(Object.keys(labelSettingsTypes).filter(type => labelSettingsTypes[type].forSiteInspection).map(type => labelSettingsTypes[type].name)));
        labels.value.forEach(label => {
            types.add(label.type);
        });
        return types;
    });
    const labelNumbers = computed(() => {
        const labelNumbers = {};
        for (const label of projector.projection.value.labels) {
            const labelType = Object.values(labelSettingsTypes).find((type) => type.name === label.type);
            if (labelType?.validityPattern) {
                const match = label.name.match(labelType.validityPattern);
                if (
                    match
                    && (
                        labelNumbers[label.type] === undefined
                        || labelNumbers[label.type].input < match.input
                    )
                ) {
                    labelNumbers[label.type] = match;
                }
            }
        }
        return labelNumbers;
    });

    provide('labelTypes', labelTypes);
    provide('labelNumbers', labelNumbers);

    /**
     * @param {{owner: string, identity: string}} options
     * @returns {Promise<unknown>}
     */
    async function initialize(options) {
        initializing.value = true;
        projector.restart();
        eventList.restart();
        if (saveHandler !== undefined) {
            saveHandler.element.removeEventListener('added', saveHandler.handler);
        }
        if (eventHandler !== undefined) {
            eventHandler.element.removeEventListener('added', eventHandler.handler);
        }
        const {promise, resolve, reject} = Promise.withResolvers();
        if (options.owner !== undefined && options.owner !== '') {
            owner.value = options.owner;
        }
        __identity.value = options.identity;
        if (owner.value === '' || __identity.value === '' || owner.value === null || __identity.value === null || owner.value === undefined || __identity.value === undefined) {
            throw new Error('initialize owner, identity');
        }
        projector.owner = toValue(owner);

        eventHandler = {
            element: eventList.eventEmitter,
            handler: event => projector.handleEvent(event.detail),
        };
        eventHandler.element.addEventListener('added', eventHandler.handler);

        if (store !== undefined) {
            const db = await (store.dbs[__identity.value]);
            const storeName = `eventStore${__identity.value}`;

            let request;
            log.debug({desc: 'before transaction initialization:', db, storeName, dbs: Object.keys(store.dbs), __identity: __identity.value});
            try {
                request = db.transaction([storeName], 'readonly').objectStore(storeName).getAll();
            } catch (e) {
                log.error(JSON.stringify({desc: 'catch initialization:', db, storeName, dbs: Object.keys(store.dbs), __identity: __identity.value}));
                throw e;
            }
            request.onsuccess = async (event) => {
                for (const inspectionEvent of event.target.result) {
                    await eventList.addEvent(inspectionEvent);
                }
                saveHandler = {
                    handler: event => {
                        store.saveEvent(__identity.value, event.detail);
                    },
                    element: eventList.eventEmitter
                };
                saveHandler.element.addEventListener('added', saveHandler.handler);
                resolve();
            };
        } else {
            resolve();
        }
        promise.then(() => initializing.value = false);
        return promise;
    }

    const debouncedRestart = debounce(async (callback) => {
        await initialize({identity: __identity.value});
        callback();
    }, 500);


    return {
        __identity: computed(() => __identity),
        additionalInformation,
        circuitComponents,
        circuits,
        error,
        examinations,
        labels,
        locations,
        migrations,
        notes,
        siteInspection: innerSiteInspection,
        timeTracks,

        initializing: computed(() => initializing),
        owner: computed(() => owner),
        projection: computed(() => projector.projection),
        events: computed(() => toValue(eventList.events)),
        eventEmitter: computed(() => eventList.eventEmitter),
        currentCircuit: computed({get: () => {
                const searchUuid = projector.projection.value.currentCircuitUuid;
                return projector.projection.value.circuits?.find(circuit => circuit.uuid === searchUuid);
            },
        }),
        currentLocation: computed({get: () => {
                const searchUuid = projector.projection.value.currentLocationUuid;
                return projector.projection.value.locations?.find(location => location.uuid === searchUuid);
            },
        }),

        addEvent: async args => {
            if (args.owner === undefined) {
                args.owner = owner.value;
            }
            return await eventList.addEvent(args);
        },
        setOwner: ownerArg => {
            owner.value = toValue(ownerArg);
        },
        setStore: innerStore => {
            if (store !== undefined) {
                throw new Error('store already set');
            }
            store = innerStore;
        },
        remove: async siteInspection => {
            if (store === undefined) {
                throw new Error('A site inspection can only be deleted if there is a store.');
            }
            await store.removeSiteInspection(siteInspection.__identity);
            ownEventEmitter.dispatchEvent(new CustomEvent('RemoveSiteInspection', {detail: siteInspection.__identity}));
        },
        addEventListener: (...args) => ownEventEmitter.addEventListener(...args),
        removeEventListener: (...args) => ownEventEmitter.removeEventListener(...args),
        initialize,
        restartProjection: (callback = () => {}) => {
            initializing.value = true;
            debouncedRestart(callback);
        },
    };
});

class SiteInspectionSyncProjector extends SiteInspectionProjector {
    handleAddEvent(event, root) {
        if (event.data.metadata?.sync.createdBy !== undefined && event.data.metadata?.sync.createdBy !== event.owner) {
            log.warn(`event data should not contain different createdBy than the event owner "${JSON.stringify(cloneDeepWith(event))}"`);
            delete event.data.metadata.sync.createdBy;
        }
        super.handleAddEvent(event, root);
        const found = this.siteInspection.value[root].find(e => e.uuid === event.data.uuid);
        const allFindings = this.siteInspection.value[root].filter(element => element.uuid === event.data?.uuid);
        if (allFindings.length > 1) {
            log.warn(`multiple candidates found: ${JSON.stringify(allFindings)}`);
        }

        if (found.metadata === undefined) {
            found.metadata = {sync: {seenBy: [], createdBy: null, editedBy: [], removedBy: [], lastChange: {}}};
        }
        found.metadata.sync.createdBy = event.owner;
        if (found.metadata.sync.seenBy === undefined) {
            found.metadata.sync.seenBy = [];
        }
        if (found.dirty === false && !found.metadata.sync.seenBy.includes(event.owner)) {
            found.metadata.sync.seenBy.push(event.owner);
        }
        if (found.dirty === true && found.metadata.sync.seenBy.includes(event.owner)) {
            found.metadata.sync.seenBy.splice(found.metadata.sync.seenBy.findIndex(seenBy => seenBy === event.owner), 1);
        }
        if (found.metadata.sync.editedBy === undefined) {
            found.metadata.sync.editedBy = [];
        }
        if (found.metadata.sync.removedBy === undefined) {
            found.metadata.sync.removedBy = [];
        }
    }

    handleEditEvent(event, root) {
        const found = this.siteInspection.value[root].find(e => e.uuid === event.data?.uuid);
        if (found === undefined) {
            log.warn(`No element found with uuid = "${event.data?.uuid}".`, event, root, JSON.stringify(this.siteInspection.value[root].map(e => e.uuid)));
            return;
        }
        const diff = calculateInnerDifference(found, event.data),
            changes = !isEqual(diff, {uuid: event.data?.uuid});

        super.handleEditEvent(event, root);
        if (found.metadata === undefined) {
            found.metadata = {sync: {seenBy: [], createdBy: null, editedBy: [], removedBy: [], lastChange: {}}};
        }
        found.metadata.sync.lastChange = {tester: event.owner, timestamp: event.occurrence};
        if (changes) {
            delete found.metadata.sync.synchronized;
        }
        if (found.metadata.sync.seenBy === undefined) {
            found.metadata.sync.seenBy = [];
        }
        if (found.dirty !== undefined && !found.metadata.sync.seenBy.includes(event.owner)) {
            found.metadata.sync.seenBy.push(event.owner);
        }
        if (diff.dirty === true && found.metadata.sync.seenBy.includes(event.owner)) {
            found.metadata.sync.seenBy.splice(found.metadata.sync.seenBy.findIndex(seenBy => seenBy === event.owner), 1);
        }
        if (found.metadata.sync.editedBy === undefined) {
            found.metadata.sync.editedBy = [];
        }
        if (
            !found.metadata.sync.editedBy.includes(event.owner)
            && (
                changes
                || found.metadata.sync.oldState !== undefined && !isEqual(calculateInnerDifference(found.metadata.sync.oldState, event.data), {uuid: found.uuid})
            )
        ) {
            found.metadata.sync.editedBy.push(event.owner);
        }
        if (found.metadata.sync.removedBy === undefined) {
            found.metadata.sync.removedBy = [];
        }
        if (
            changes
            && (
                found.metadata.sync.editedBy.length > 1
                || found.metadata.sync.removedBy.length > 0
            )
        ) {
            found.metadata.sync.conflict = true;
        }
    }

    handleRemoveEvent(event, root) {
        const found = this.siteInspection.value[root].find(e => e.uuid === event.data.uuid);
        if (found === undefined) {
            log.warn(`No element found with uuid = "${event.data?.uuid}".`, event);
            return;
        }
        delete found.metadata.sync.synchronized;
        if (!found.metadata.sync.removedBy.includes(event.owner)) {
            found.metadata.sync.removedBy.push(event.owner);
        }
        if (found.metadata.sync.createdBy !== event.owner) {
            found.metadata.sync.conflict = true;
        }
        found.metadata.sync.lastChange = {tester: event.owner, timestamp: event.occurrence};
        this.removed[root].push(event.data.uuid);
    }

    handleSynchronizedEvent(event) {
        forEachSiteInspectionElement(this.siteInspection.value, (element, root) => {
            const participants = new Set([]);
            try {
                participants.add(element.metadata.sync.createdBy);
                for (const seenBy of element.metadata.sync.seenBy) {
                    participants.add(seenBy);
                }
                for (const editedBy of element.metadata.sync.editedBy) {
                    participants.add(editedBy);
                }
                for (const removedBy of element.metadata.sync.removedBy) {
                    participants.add(removedBy);
                }
                for (const participant of event?.testers ?? participants) {
                    participants.delete(participant);
                }
            } catch (e) {
            }
            if (participants.size === 0) {
                if (element.metadata.sync.removedBy.length > 0) {
                    const findIndex = this.siteInspection.value[root].findIndex(e => e.uuid === element.uuid);
                    if (findIndex >= 0) {
                        this.siteInspection.value[root].splice(findIndex, 1);
                    }
                    return;
                }
                if (element.metadata.sync.seenBy.length > 0 && element.dirty !== undefined && element.dirty !== null) {
                    element.dirty = false;
                }
                delete element.metadata.sync.conflict;
                element.metadata.sync.editedBy = [];
                element.metadata.sync.synchronized = true;
                element.metadata.sync.oldState = cloneDeepWith(element);
                delete element.metadata.sync.oldState.metadata;
            }
        });
        return super.handleSynchronizedEvent(event);
    }
}

export {SiteInspectionIndexedStore, SiteInspectionProjector, EventList, Event, useSiteInspectionStore, SiteInspectionSyncProjector, calculateInnerDifference};
